import React, { useState, useEffect } from 'react';
import { Tooltip, CircularLoader } from '@dhis2/ui';

function AddObjectiveButton({
  onAdd,
  reportingToObjectives = [],
  myObjectives = [],
  goalItems = [],
  currentUserPosition = null,
  loading = false,
  goalsFullyLoaded = false,
  managerObjectivesLoaded = false,
  protectedObjectives = new Set(),
  childCounts = {},
  creationAllowed = true,
}) {
  const [hovered, setHovered] = useState(false);

  const isLoading = loading || !goalsFullyLoaded || !managerObjectivesLoaded;

  // Determine if user is top-level (CEO/Admin) - NO reporting-to attribute
  let isTopLevelUser = false;
  if (currentUserPosition?.attributes) {
    const reportingToAttr = currentUserPosition.attributes.find(
      attr => attr.attribute === 'SjP47qi3JeB'
    );
    isTopLevelUser = !reportingToAttr?.value;
  }

  // ✅ Debug: Log when props change - MOVED AFTER isTopLevelUser is defined
  useEffect(() => {
    console.log('🔍 [AddObjectiveButton] Props updated:', {
      myObjectivesCount: myObjectives.length,
      myObjectivesStatuses: myObjectives.map(obj => ({ 
        id: obj.id, 
        name: obj.name, 
        approvalStatus: obj.approvalStatus 
      })),
      approvedCount: myObjectives.filter(obj => obj.approvalStatus === 'approved').length,
      isTopLevelUser,
      creationAllowed,
      goalsFullyLoaded,
      managerObjectivesLoaded,
    });
  }, [myObjectives, isTopLevelUser, creationAllowed, goalsFullyLoaded, managerObjectivesLoaded]);

  // ✅ FIXED LOCK LOGIC
  const getLockStatus = () => {
    console.log('🔍 [AddObjectiveButton] getLockStatus called:', {
      isTopLevelUser,
      myObjectivesCount: myObjectives.length,
      approvedCount: myObjectives.filter(obj => obj.approvalStatus === 'approved').length,
      myObjectives: myObjectives.map(obj => ({ name: obj.name, status: obj.approvalStatus }))
    });

    // ✅ 1. TOP-LEVEL USERS: Block if they have approved objectives
    if (isTopLevelUser) {
      // Check if goals exist
      if (goalItems.length === 0) {
        return {
          locked: true,
          reason: 'No company goals available for the current fiscal year. Please create company goals first.',
          type: 'prerequisite',
        };
      }
      
      // ✅ CRITICAL FIX: Check for approved objectives
      const approvedObjs = myObjectives.filter(obj => obj.approvalStatus === 'approved');
      if (approvedObjs.length > 0) {
        const details = approvedObjs.map(obj => `- ${obj.name}`).join('\n');
        console.log('🔒 [AddObjectiveButton] Top-level user has approved objectives, locking button:', {
          count: approvedObjs.length,
          details
        });
        return {
          locked: true,
          reason: `You have ${approvedObjs.length} finalized objective(s). As a top-level user, you cannot create new objectives once you have finalized existing ones.\n\nFinalized Objectives:\n${details}`,
          type: 'approved',
        };
      }

      // ✅ PROTECT: Check for protected objectives (linked to subordinates)
      const protectedObjs = myObjectives.filter(obj => protectedObjectives.has(obj.id));
      if (protectedObjs.length > 0) {
        const details = protectedObjs
          .map(obj => {
            const count = childCounts[obj.id] || 0;
            return `- ${obj.name} (${count} aligned objective${count !== 1 ? 's' : ''})`;
          })
          .join('\n');
        console.log('🔒 [AddObjectiveButton] Top-level user has protected objectives, locking button:', {
          count: protectedObjs.length,
          details
        });
        return {
          locked: true,
          reason: `You have ${protectedObjs.length} protected objective(s) that are linked to staff reporting to you. You cannot create new objectives while these are active.\n\nProtected Objectives:\n${details}`,
          type: 'protected',
        };
      }

      return { locked: false, reason: '' };
    }

    // ============================================================
    // NON-TOP-LEVEL USERS: Apply all restrictions
    // ============================================================

    // ✅ 2. Manager approval check
    if (!creationAllowed) {
      const pendingCount = reportingToObjectives.filter(obj => obj.approvalStatus !== 'approved').length;
      return {
        locked: true,
        reason: `Cannot create objectives. ${pendingCount} manager objective(s) are pending approval. Please wait for your manager to approve their objectives first.`,
        type: 'manager_approval',
      };
    }

    // ✅ 3. Check if manager has any objectives
    const hasManagerObjectives = reportingToObjectives.length > 0;
    const hasUserObjectives = myObjectives.length > 0;
    
    if (!hasManagerObjectives && !hasUserObjectives) {
      return {
        locked: true,
        reason: 'Your manager has not created any objectives for this fiscal year. Please wait for your manager to create objectives first.',
        type: 'prerequisite',
      };
    }

    // ✅ 4. APPROVED OBJECTIVES LOCK - for non-top-level users
    const approvedObjs = myObjectives.filter(obj => obj.approvalStatus === 'approved');
    if (approvedObjs.length > 0) {
      const details = approvedObjs.map(obj => obj.name).join('\n');
      return {
        locked: true,
        reason: `You have ${approvedObjs.length} approved objective(s) that are locked. You must request your manager to return these objectives before creating new ones.\n\n${details}`,
        type: 'approved',
      };
    }

    // ✅ 5. Protected objectives with children
    const protectedObjs = myObjectives.filter(obj => protectedObjectives.has(obj.id));
    if (protectedObjs.length > 0) {
      const details = protectedObjs
        .map(obj => {
          const count = childCounts[obj.id] || 0;
          return `${obj.name} (${count} aligned objective${count !== 1 ? 's' : ''})`;
        })
        .join('\n');
      return {
        locked: true,
        reason: `You have ${protectedObjs.length} protected objective(s) with linked objectives. You must ask the staff member(s) to unlink or delete the aligned objectives first before you can create new objectives.\n\n${details}`,
        type: 'protected',
      };
    }

    // ✅ All checks passed - allow creation
    return { locked: false, reason: '' };
  };

  let allowed = true;
  let reason = '';

  if (!isLoading) {
    const lockStatus = getLockStatus();
    allowed = !lockStatus.locked;
    reason = lockStatus.reason;
  }

  // Debug log
  console.log('🔍 [AddObjectiveButton] Final State:', {
    isLoading,
    allowed,
    reason,
    isTopLevelUser,
    creationAllowed,
    myObjectivesCount: myObjectives.length,
    approvedCount: myObjectives.filter(obj => obj.approvalStatus === 'approved').length,
  });

  const baseStyle = {
    position: 'fixed',
    bottom: '30px',
    right: '30px',
    zIndex: 1000,
  };

  const getButtonColor = () => {
    if (isLoading) return '#9e9e9e';
    if (!allowed) return '#9e9e9e';
    return '#1c9420';
  };

  const buttonStyle = {
    borderRadius: hovered ? '24px' : '50%',
    border: 'none',
    width: hovered ? '180px' : '50px',
    height: '50px',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: getButtonColor(),
    color: '#fff',
    cursor: !isLoading && allowed ? 'pointer' : 'not-allowed',
    boxShadow: '0 8px 16px rgba(122, 118, 118, 1)',
    transition: 'width 0.3s ease, border-radius 0.3s ease, padding 0.3s ease',
    overflow: 'hidden',
    padding: hovered ? '0 20px' : '0',
    opacity: isLoading ? 0.7 : 1,
  };

  const handleClick = () => {
    if (!isLoading && allowed && onAdd) {
      onAdd();
    } else if (!allowed && reason) {
      alert(`❌ Cannot Add Objective\n\n${reason}`);
    }
  };

  const getButtonText = () => {
    if (isLoading) return '⏳';
    
    // Top-level users
    if (isTopLevelUser) {
      const approvedCount = myObjectives.filter(obj => obj.approvalStatus === 'approved').length;
      if (approvedCount > 0) {
        if (hovered) return `🔒 ${approvedCount} Finalized`;
        return '+';
      }
      const protectedCount = myObjectives.filter(obj => protectedObjectives.has(obj.id)).length;
      if (protectedCount > 0) {
        if (hovered) return `🔒 ${protectedCount} Protected`;
        return '+';
      }
      if (hovered) return '+ Add Objective';
      return '+';
    }
    
    // Non-top-level users
    if (hovered && allowed) {
      const pendingCount = reportingToObjectives.filter(obj => obj.approvalStatus !== 'approved').length;
      if (pendingCount > 0) {
        return `⏳ ${pendingCount} Pending`;
      }
      const approvedCount = myObjectives.filter(obj => obj.approvalStatus === 'approved').length;
      if (approvedCount > 0) {
        return '🔒 Locked';
      }
      return '+ Add Objective';
    }
    if (hovered && !allowed) {
      return '🔒 Locked';
    }
    return '+';
  };

  // Show badge on the button
  const getBadge = () => {
    if (!hovered) {
      const approvedCount = myObjectives.filter(obj => obj.approvalStatus === 'approved').length;
      const protectedCount = myObjectives.filter(obj => protectedObjectives.has(obj.id)).length;
      
      if (approvedCount > 0 && !allowed) {
        return (
          <span style={{
            position: 'absolute',
            top: '-5px',
            right: '-5px',
            backgroundColor: isTopLevelUser ? '#ffc107' : '#dc3545',
            color: isTopLevelUser ? '#333' : 'white',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            fontSize: '10px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}>
            {approvedCount}
          </span>
        );
      }
      
      if (protectedCount > 0 && !allowed) {
        return (
          <span style={{
            position: 'absolute',
            top: '-5px',
            right: '-5px',
            backgroundColor: '#dc3545',
            color: 'white',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            fontSize: '10px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}>
            {protectedCount}
          </span>
        );
      }
      
      if (!isTopLevelUser) {
        const pendingCount = reportingToObjectives.filter(obj => obj.approvalStatus !== 'approved').length;
        if (pendingCount > 0) {
          return (
            <span style={{
              position: 'absolute',
              top: '-5px',
              right: '-5px',
              backgroundColor: '#ffc107',
              color: '#333',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              fontSize: '10px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            }}>
              {pendingCount}
            </span>
          );
        }
      }
    }
    return null;
  };

  if (isLoading) {
    return (
      <div style={baseStyle}>
        <div style={buttonStyle}>
          <CircularLoader small />
        </div>
      </div>
    );
  }

  const buttonElement = (
    <div style={{ position: 'relative' }}>
      <button
        onClick={handleClick}
        style={buttonStyle}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        disabled={!allowed}
      >
        {getButtonText()}
      </button>
      {getBadge()}
    </div>
  );

  if (!allowed) {
    return (
      <div style={baseStyle}>
        <Tooltip content={reason} placement="left">
          {buttonElement}
        </Tooltip>
      </div>
    );
  }

  return <div style={baseStyle}>{buttonElement}</div>;
}

export default AddObjectiveButton;